import React, { useEffect, useState, useMemo, useRef } from "react";
import { Layout } from "antd";
import "leaflet/dist/leaflet.css";
import * as turf from "@turf/turf";
// import logo from "../assets/Logo T&G.png"; // not used when header is hidden
import chroma from "chroma-js";
import { Grid } from "antd";
import Papa from "papaparse";
import MapComponent from "./Mappa";
import SiderComponent from "./Sider"; // reused as popup card
// Logo imports removed - footer simplified
import { FaLongArrowAltDown, FaLongArrowAltUp } from "react-icons/fa";
import BetwinLogo from "../assets/exis.svg";
import "../styles/GeoJson.css";
import {
  fetchMetricsData,
  fetchNetworkOffsetData,
  fetchTrafficCSV,
  fetchTrafficZoneData,
  fetchMapGeoJSON,
  fetchArchiZonaGeoJSON,
} from "../APIs/apiService";
const { useBreakpoint } = Grid;

const { Content } = Layout;

function GeoJson() {
  const [geoData, setGeoData] = useState(null);
  const [dbData, setDbData] = useState(null);
  const [selectedHash, setSelectedHash] = useState([]);
  const [highlightedHash, setHighlightedHash] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  // const [collapsed, setCollapsed] = useState(false);
  const [siderSection, setSiderSection] = useState();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [speedDomain, setSpeedDomain] = useState([0, 1]);
  const [colorScale, setColorScale] = useState(() =>
    chroma.scale(["red", "yellow", "green"]).domain([0, 1])
  );
  const [showDeckGL, setShowDeckGL] = useState(false);
  const [mapMode, setMapMode] = useState("mappa"); // 'mappa', 'genova', 'nuvola'
  const [footerOpen, setFooterOpen] = useState(true);
  const [radarSelected, setRadarSelected] = useState(false);
  const [isLoadingGeoData, setIsLoadingGeoData] = useState(true);
  const hasFetchedDbData = useRef(false);

  // Network view mode state (when no road is selected)
  const [networkViewCategory, setNetworkViewCategory] = useState(null); // 'velocita', 'flow', 'traveltime'
  const [networkViewDate, setNetworkViewDate] = useState(null);
  const [networkViewTime, setNetworkViewTime] = useState(null);
  const [availableNetworkDates, setAvailableNetworkDates] = useState([]);
  const [availableNetworkTimes, setAvailableNetworkTimes] = useState([]);

  // Responsive footer height and handle size based on viewport/breakpoints
  const FOOTER_HEIGHT = screens.xl
    ? 40
    : screens.lg
    ? 40
    : screens.md
    ? 40
    : 40;
  // handle height no longer used (floating toggle replaces it)

  useEffect(() => {
    // Prevent multiple fetches
    if (hasFetchedDbData.current) {
      console.log("GeoJSON: Already fetched dbData, skipping...");
      return;
    }

    hasFetchedDbData.current = true;
    console.log("GeoJSON: Component mounted, fetching metrics.json...");

    // Fetch metrics data from metrics.json
    fetchMetricsData()
      .then((data) => {
        console.log("GeoJSON: Successfully fetched metrics.json, setting dbData");

        // Transform metrics.json data to match expected format
        // The metrics.json has UnifiedID instead of hash
        const transformedData = data.map(item => {
          // Parse date from day_group_label
          // Supports both formats:
          // - New format: "2024-08-01" (YYYY-MM-DD)
          // - Old format: "12,13,14-03-2024_0" (DD,DD,DD-MM-YYYY_0)
          const dayGroupParts = item.day_group_label.split('_')[0];
          let formattedDate = '';
          let timestamp = 0;

          // Check if it's new format (YYYY-MM-DD)
          const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          // Check if it's old format (DD,DD,DD-MM-YYYY or DD-MM-YYYY)
          const oldFormatMatch = dayGroupParts.match(/(\d+),?(\d+),?(\d+)-(\d+)-(\d+)/);

          if (newFormatMatch) {
            // New format: YYYY-MM-DD
            formattedDate = dayGroupParts; // Already in correct format

            // Calculate timestamp for sorting
            const [hour, minute] = (item.t_start || '00:00').split(':');
            const dateObj = new Date(`${formattedDate}T${hour}:${minute}:00`);
            timestamp = Math.floor(dateObj.getTime() / 1000);
          } else if (oldFormatMatch) {
            // Old format: Use the last day in the group for the date
            const day = oldFormatMatch[3].padStart(2, '0');
            const month = oldFormatMatch[4].padStart(2, '0');
            const year = oldFormatMatch[5];
            formattedDate = `${year}-${month}-${day}`;

            // Calculate timestamp for sorting
            const [hour, minute] = (item.t_start || '00:00').split(':');
            const dateObj = new Date(`${formattedDate}T${hour}:${minute}:00`);
            timestamp = Math.floor(dateObj.getTime() / 1000);
          }

          return {
            // Keep original metrics.json fields
            ...item,
            // Map UnifiedID to hash for compatibility with existing components
            hash: item.UnifiedID,
            // Add date/time fields for compatibility
            date: formattedDate + 'T00:00:00.000',
            timestamp: timestamp,
            time: timestamp,
            hour: parseInt((item.t_start || '00:00').split(':')[0]),
            // Map to common field names used by components
            speed: item.AvgSp,
            travel_time_in_seconds: item.AvgTt,
            length_in_meters: item.len_sum_m,
            // Note: metrics.json doesn't have these fields, removing them:
            // - historic_traffic_travel_time_in_seconds
            // - traffic_length_in_meters
            // - live_traffic_incidents_travel_time_in_seconds
            // - traffic_delay_in_seconds
          };
        });

        setDbData(transformedData);
      })
      .catch((error) => {
        console.error("Failed to load metrics.json:", error);
        // Try old fallback methods
        console.warn("Falling back to Traffic_5min.csv...");

        fetchTrafficCSV()
          .then((csvText) => {
            if (typeof csvText === 'object') {
              // If it's already JSON (redirected to metrics), use it directly
              setDbData(csvText);
              return;
            }

            // Parse CSV to JSON
            Papa.parse(csvText, {
              header: true,
              dynamicTyping: true,
              skipEmptyLines: true,
              complete: (results) => {
                console.log("GeoJSON: Successfully parsed CSV, setting dbData");
                setDbData(results.data);
              },
              error: (error) => {
                console.error("Error parsing CSV:", error);
                // Final fallback
                fetchTrafficZoneData().then((data) => {
                  console.log("GeoJSON: Successfully fetched db_dati_zona.json (final fallback)");
                  setDbData(data);
                });
              },
            });
          })
          .catch((finalError) => {
            console.error("All data fetching methods failed:", finalError);
          });
      });
  }, []);

  // Extract available dates and times for network view from metrics.json
  useEffect(() => {
    if (!dbData || dbData.length === 0) return;

    const dates = new Set();
    const times = new Set();

    dbData.forEach((d) => {
      // Extract dates from day_group_label field
      // Supports both formats:
      // - New format: "2024-08-01" (YYYY-MM-DD)
      // - Old format: "12,13,14-03-2024_0" (DD,DD,DD-MM-YYYY_0)
      if (d.day_group_label) {
        const dayGroupParts = d.day_group_label.split('_')[0];

        // Check if it's new format (YYYY-MM-DD)
        const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (newFormatMatch) {
          // New format: already in YYYY-MM-DD
          dates.add(dayGroupParts);
        } else {
          // Old format: Split by hyphen to separate days from month-year
          const parts = dayGroupParts.split('-');
          if (parts.length === 3) {
            const daysStr = parts[0]; // "12,13,14"
            const month = parts[1].padStart(2, '0'); // "03"
            const year = parts[2]; // "2024"

            // Split days by comma
            const days = daysStr.split(',');
            days.forEach(day => {
              if (day && day.trim()) {
                const dayPadded = day.trim().padStart(2, '0');
                dates.add(`${year}-${month}-${dayPadded}`);
              }
            });
          }
        }
      } else if (d.timestamp_utc) {
        // Fallback to old format
        const [date, time] = d.timestamp_utc.split(" ");
        dates.add(date);
        times.add(time.split("+")[0]); // Remove timezone
      } else if (d.date) {
        dates.add(d.date.split("T")[0]);
      }

      // Extract times from t_start field (e.g., "00:00")
      if (d.t_start) {
        times.add(d.t_start + ":00"); // Add seconds for consistency
      }
    });

    const sortedDates = Array.from(dates).sort();
    const sortedTimes = Array.from(times).sort();

    setAvailableNetworkDates(sortedDates);
    setAvailableNetworkTimes(sortedTimes);

    // Set initial date and time if available - use LAST date (14th)
    if (sortedDates.length > 0 && !networkViewDate) {
      setNetworkViewDate(sortedDates[sortedDates.length - 1]);
    }
    if (sortedTimes.length > 0 && !networkViewTime) {
      setNetworkViewTime(sortedTimes[0]);
    }
  }, [dbData, networkViewDate, networkViewTime]);

  // Listen for highlightRoad event from markers
  useEffect(() => {
    const handleHighlightRoad = (event) => {
      const { hash } = event.detail;
      setHighlightedHash(hash);
    };

    const handleResetHighlight = () => {
      setHighlightedHash(null);
    };

    window.addEventListener('highlightRoad', handleHighlightRoad);
    window.addEventListener('resetHighlight', handleResetHighlight);

    return () => {
      window.removeEventListener('highlightRoad', handleHighlightRoad);
      window.removeEventListener('resetHighlight', handleResetHighlight);
    };
  }, []);

  // Load GeoJSON only once and enrich with ALL data
  useEffect(() => {
    if (!dbData) {
      console.log("GeoJSON: Waiting for dbData...");
      return;
    }

    console.log("GeoJSON: Starting to load geo data...");
    setIsLoadingGeoData(true);

    // Fetch network offset data (GPKG converted to GeoJSON)
    fetchNetworkOffsetData()
      .then((data) => {
        try {
          const js = offsetSegmentiNegativi(data, 4);

          // Create a lookup map for faster access: hash -> array of all records
          const dataByHash = {};
          dbData.forEach((d) => {
            if (!dataByHash[d.UnifiedID]) {
              dataByHash[d.UnifiedID] = [];
            }
            dataByHash[d.UnifiedID].push(d);
          });

          const enriched = js.features.map((f) => {
            const allRecords = dataByHash[f.properties.hash] || [];

            // Store all records for this hash so we can filter later
            return {
              ...f,
              properties: {
                ...f.properties,
                allRecords: allRecords, // Store all data records
              },
            };
          });

          const fullGeo = { ...js, features: enriched };
          setGeoData(fullGeo);
          console.log("GeoJSON: Successfully loaded network offset data");
          setIsLoadingGeoData(false);
        } catch (processingError) {
          console.error("Error processing network offset data:", processingError);
          setIsLoadingGeoData(false);
          throw processingError;
        }
      })
      .catch((error) => {
        console.warn(
          "Failed to load network offset data, trying fallback:",
          error
        );

        // Fallback to old geojson methods
        return fetchMapGeoJSON()
          .then((data) => {
            try {
              const js = offsetSegmentiNegativi(data, 4);

              // Create a lookup map for faster access
              const dataByHash = {};
              dbData.forEach((d) => {
                if (!dataByHash[d.hash]) {
                  dataByHash[d.hash] = [];
                }
                dataByHash[d.hash].push(d);
              });

              const enriched = js.features.map((f) => {
                const allRecords = dataByHash[f.properties.hash] || [];

                return {
                  ...f,
                  properties: {
                    ...f.properties,
                    allRecords: allRecords,
                  },
                };
              });

              const fullGeo = { ...js, features: enriched };
              setGeoData(fullGeo);
              console.log(
                "GeoJSON: Successfully loaded Map.geojson (fallback)"
              );
              setIsLoadingGeoData(false);
            } catch (processingError) {
              console.error(
                "Error processing Map.geojson:",
                processingError
              );
              setIsLoadingGeoData(false);

              // Try final fallback to archi_zona
              return fetchArchiZonaGeoJSON()
                .then((data) => {
                  const js = offsetSegmentiNegativi(data, 4);
                  const dataByHash = {};
                  dbData.forEach((d) => {
                    if (!dataByHash[d.hash]) {
                      dataByHash[d.hash] = [];
                    }
                    dataByHash[d.hash].push(d);
                  });
                  const enriched = js.features.map((f) => {
                    const allRecords = dataByHash[f.properties.hash] || [];
                    return {
                      ...f,
                      properties: {
                        ...f.properties,
                        allRecords: allRecords,
                      },
                    };
                  });
                  const fullGeo = { ...js, features: enriched };
                  setGeoData(fullGeo);
                  console.log("GeoJSON: Successfully loaded archi_zona.geojson (final fallback)");
                  setIsLoadingGeoData(false);
                })
                .catch((finalError) => {
                  console.error("Error loading GeoJSON (all attempts failed):", finalError);
                  setIsLoadingGeoData(false);
                });
            }
          })
          .catch((fallbackError) => {
            console.error(
              "Error loading GeoJSON (intermediate fallback failed):",
              fallbackError
            );
            setIsLoadingGeoData(false);
          });
      });
  }, [dbData]); // Only depend on dbData, load once!

  // Separate effect to recalculate colors based on network view settings
  useEffect(() => {
    if (!geoData || !geoData.features || geoData.features.length === 0) return;

    console.log("Recalculating color scale...");

    // Get the current values for coloring based on network view settings
    const values = geoData.features.map((f) => {
      const allRecords = f.properties.allRecords || [];

      // Find the record for the selected date/time if in network view
      let rec;
      if (
        networkViewCategory &&
        networkViewDate &&
        networkViewTime &&
        selectedHash.length === 0
      ) {
        // Match by date AND time
        rec = allRecords.find((d) => {
          if (d.day_group_label) {
            const dayGroupParts = d.day_group_label.split('_')[0];

            // Check if it's new format (YYYY-MM-DD)
            const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (newFormatMatch) {
              // New format: direct comparison
              if (dayGroupParts === networkViewDate) {
                const recordTime = (d.t_start || '00:00') + ':00';
                if (recordTime === networkViewTime) {
                  return true;
                }
              }
            } else {
              // Old format: parse DD,DD,DD-MM-YYYY
              const parts = dayGroupParts.split('-');
              if (parts.length === 3) {
                const daysStr = parts[0];
                const month = parts[1].padStart(2, '0');
                const year = parts[2];

                const days = daysStr.split(',');
                for (const day of days) {
                  if (day && day.trim()) {
                    const dayPadded = day.trim().padStart(2, '0');
                    const recordDate = `${year}-${month}-${dayPadded}`;
                    if (recordDate === networkViewDate) {
                      // Also check time match
                      const recordTime = (d.t_start || '00:00') + ':00';
                      if (recordTime === networkViewTime) {
                        return true;
                      }
                    }
                  }
                }
              }
            }
          }
          return false;
        }) || allRecords[0] || {};
      } else {
        // Use first available record as default
        rec = allRecords[0] || {};
      }

      // Get the value based on category using metrics.json fields
      if (networkViewCategory === "velocita") {
        return parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      } else if (networkViewCategory === "flow") {
        const value = parseFloat(rec.Flusso_Espanso) || parseFloat(rec.flow) || 0;
        // Divide flow value by 3 as requested
        return value / 3;
      } else if (networkViewCategory === "traveltime") {
        // Return travel time in seconds
        return parseFloat(rec.AvgTt) || parseFloat(rec.travel_time_in_seconds) || 0;
      } else {
        // Default to speed
        return parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      }
    });

    // Calculate min and max for color scale
    const validValues = values.filter((v) => v > 0);
    if (validValues.length === 0) return;

    let minS = Math.min(...validValues);
    let maxS = Math.max(...validValues);

    // Ensure some spread in the scale
    if (minS === maxS) {
      minS = minS * 0.9;
      maxS = maxS * 1.1;
    }

    const scale = chroma
      .scale(
        networkViewCategory === "velocita"
          ? ["red", "yellow", "green"]  // Low speed = red, high speed = green
          : networkViewCategory === "flow"
          ? ["green", "yellow", "red"]   // Low flow = green, high flow = red
          : networkViewCategory === "traveltime"
          ? ["green", "yellow", "red"]   // Low travel time = green, high travel time = red
          : ["red", "yellow", "green"]
      )
      .domain([minS, maxS]);

    setSpeedDomain([minS, maxS]);
    setColorScale(() => scale);

    console.log(`Color scale updated: ${minS.toFixed(2)} - ${maxS.toFixed(2)}`);
  }, [
    geoData,
    networkViewCategory,
    networkViewDate,
    networkViewTime,
    selectedHash.length,
  ]);

  const styleFeature = (feature) => {
    const isSel = selectedHash.includes(feature.properties.hash);
    // Only highlight if highlightedHash is set AND matches the street name
    const isHighlighted = highlightedHash && highlightedHash === feature.properties.StreetName;

    // Get the appropriate record based on network view settings
    const allRecords = feature.properties.allRecords || [];
    let rec;

    if (
      networkViewCategory &&
      networkViewDate &&
      networkViewTime &&
      selectedHash.length === 0
    ) {
      // In network view mode, filter by selected date/time
      rec = allRecords.find((d) => {
        // Match by date from day_group_label
        if (d.day_group_label) {
          const dayGroupParts = d.day_group_label.split('_')[0];

          // Check if it's new format (YYYY-MM-DD)
          const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (newFormatMatch) {
            // New format: direct comparison
            if (dayGroupParts === networkViewDate) {
              const recordTime = (d.t_start || '00:00') + ':00';
              if (recordTime === networkViewTime) {
                return true;
              }
            }
          } else {
            // Old format: parse DD,DD,DD-MM-YYYY
            const parts = dayGroupParts.split('-');
            if (parts.length === 3) {
              const daysStr = parts[0];
              const month = parts[1].padStart(2, '0');
              const year = parts[2];

              const days = daysStr.split(',');
              for (const day of days) {
                if (day && day.trim()) {
                  const dayPadded = day.trim().padStart(2, '0');
                  const recordDate = `${year}-${month}-${dayPadded}`;
                  if (recordDate === networkViewDate) {
                    // Also check time match
                    const recordTime = (d.t_start || '00:00') + ':00';
                    if (recordTime === networkViewTime) {
                      return true;
                    }
                  }
                }
              }
            }
          }
        }
        return false;
      }) || allRecords[0] || {};
    } else {
      rec = allRecords[0] || {};
    }

    // Determine which value to use for coloring based on metrics.json fields
    let value;
    let hasData = false;

    if (networkViewCategory) {
      if (networkViewCategory === "velocita") {
        // Use AvgSp field from metrics.json
        value = parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
        hasData = value > 0;
      } else if (networkViewCategory === "flow") {
        // Use Flusso_Espanso as flow indicator (VEH/15min) - divided by 3
        value = (parseFloat(rec.Flusso_Espanso) || parseFloat(rec.flow) || 0) / 3;
        hasData = value >= 0;
      } else if (networkViewCategory === "traveltime") {
        // Use AvgTt field from metrics.json (travel time in seconds)
        value = parseFloat(rec.AvgTt) || parseFloat(rec.travel_time_in_seconds) || 0;
        hasData = value > 0;
      }
    } else {
      // Default to speed from metrics.json
      value = parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      hasData = value > 0;
    }

    // Determine color: blue if highlighted, purple if selected, darker cyan if no data, otherwise use color scale
    let color;
    if (isHighlighted) {
      color = "blue";
    } else if (isSel) {
      color = "purple";
    } else if (!hasData || allRecords.length === 0) {
      color = "#2C7A7B"; // Darker cyan/teal for roads without data - visible but not too bright
    } else {
      color = colorScale(value).hex();
    }

    return {
      color: color,
      weight: isSel || isHighlighted ? 6 : 4,
      opacity: 1,
      lineCap: "round",
    };
  };

  const offsetSegmentiNegativi = (data, distanza = 4) => {
    const offsetFeatures = data.features.map((f) => {
      const segId = f.properties["Segment Id"];
      if (String(segId).startsWith("-")) {
        try {
          const offsetLine = turf.lineOffset(f, distanza, { units: "meters" });
          return { ...f, geometry: offsetLine.geometry };
        } catch (e) {
          console.warn("Offset fallito per segmento", segId);
          return f;
        }
      }
      return f;
    });
    return { ...data, features: offsetFeatures };
  };

  const onFeatureSelect = (hash, featureProperties) => {
    setSelectedHash((prevHash) => {
      // Normalize to array
      const currentRoads = Array.isArray(prevHash)
        ? prevHash
        : prevHash
        ? [prevHash]
        : [];

      // Check if this road is already selected
      const isAlreadySelected = currentRoads.includes(hash);

      if (isAlreadySelected) {
        // DESELECT: Remove this road
        const newHash = currentRoads.filter((h) => h !== hash);

        setSelectedFeature((prevFeatures) => {
          const currentFeatures = Array.isArray(prevFeatures)
            ? prevFeatures
            : prevFeatures
            ? [prevFeatures]
            : [];
          return currentFeatures.filter((f) => f.hash !== hash);
        });

        // If we're deselecting and no roads remain, close the sider
        if (newHash.length === 0 && !radarSelected) {
          setSiderSection(null);
        }

        return newHash;
      } else {
        // SELECT: Add this road (enforce 2-element total limit)
        const radarCount = radarSelected ? 1 : 0;
        const totalSelected = currentRoads.length + radarCount;

        // Close network view card when selecting a road
        if (currentRoads.length === 0 && networkViewCategory) {
          // Opening from network view - keep the same section but it will show road data
          // The section stays the same (velocita, congestion, anomaly) but shows road-specific data
        }

        if (totalSelected >= 2) {
          // At capacity (2 elements selected)
          // FIFO: Remove oldest element to make room

          if (currentRoads.length === 2) {
            // Have 2 roads → remove oldest road
            const newHash = [currentRoads[1], hash];
            setSelectedFeature((prevFeatures) => {
              const currentFeatures = Array.isArray(prevFeatures)
                ? prevFeatures
                : prevFeatures
                ? [prevFeatures]
                : [];
              return [currentFeatures[1], featureProperties];
            });
            return newHash;
          } else if (currentRoads.length === 1 && radarSelected) {
            // Have 1 road + 1 radar → remove radar to add 2nd road
            setRadarSelected(false);
            const newHash = [...currentRoads, hash];
            setSelectedFeature((prevFeatures) => {
              const currentFeatures = Array.isArray(prevFeatures)
                ? prevFeatures
                : prevFeatures
                ? [prevFeatures]
                : [];
              return [...currentFeatures, featureProperties];
            });
            return newHash;
          }
        }

        // Under capacity → just add it
        const newHash = [...currentRoads, hash];
        setSelectedFeature((prevFeatures) => {
          const currentFeatures = Array.isArray(prevFeatures)
            ? prevFeatures
            : prevFeatures
            ? [prevFeatures]
            : [];
          return [...currentFeatures, featureProperties];
        });
        return newHash;
      }
    });

    // Update dates based on the clicked hash - always select LAST date
    const dates = [
      ...new Set(
        dbData
          ?.filter((d) => d.hash === hash && d.date)
          .map((d) => d.date.split("T")[0])
      ),
    ].sort();

    // Always select the LAST available date (14th)
    if (dates.length > 0) {
      setSelectedDate(dates[dates.length - 1]);
    } else {
      setSelectedDate(null);
    }
  };

  const onRadarSelect = () => {
    setRadarSelected((prev) => {
      const willBeSelected = !prev;

      if (willBeSelected) {
        // SELECTING radar - enforce 2-element limit
        const currentRoads = Array.isArray(selectedHash)
          ? selectedHash
          : selectedHash
          ? [selectedHash]
          : [];
        const totalSelected = currentRoads.length; // radar not counted yet

        if (totalSelected >= 2) {
          // Have 2 roads → remove oldest road to make room for radar
          const newHash = currentRoads.slice(1);
          setSelectedHash(newHash);
          setSelectedFeature((prevFeatures) => {
            const currentFeatures = Array.isArray(prevFeatures)
              ? prevFeatures
              : prevFeatures
              ? [prevFeatures]
              : [];
            return currentFeatures.slice(1);
          });
        }
        // else: Have 0 or 1 roads → just add radar (total will be 1 or 2)
      }
      // DESELECTING radar → just remove it (don't touch roads)

      return willBeSelected;
    });
  };

  // const arco = dbData?.find((d) => d.hash === selectedHash);

  // Extract available dates from the traffic data
  const availableDates = useMemo(() => {
    if (!dbData || !selectedHash || selectedHash.length === 0) return [];

    const dates = new Set();
    dbData
      .filter((d) => selectedHash.includes(d.UnifiedID))
      .forEach((d) => {
        // Support both new and old date formats
        if (d.timestamp_utc) {
          // New format: "2025-05-10 07:50:23+00:00"
          const date = d.timestamp_utc.split(" ")[0];
          dates.add(date);
        } else if (d.date) {
          // Old format: "2024-11-22T00:00:00.000"
          const date = d.date.split("T")[0];
          dates.add(date);
        }
      });

    return Array.from(dates).sort();
  }, [dbData, selectedHash]);

  /*
  const selectedData = dbData
    ?.filter((d) => d.hash === selectedHash && d.date?.startsWith(selectedDate))
    ?.sort((a, b) => a.timestamp - b.timestamp)
    ?.map((d) => {
      const date = new Date(d.timestamp * 1000);
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      const orario = `${hh}:${mm}:${ss}`;

      const velocita =
        d.travel_time_in_seconds > 0
          ? (d.length_in_meters / d.travel_time_in_seconds) * 3.6
          : 0;

      return {
        time: orario,
        velocità: Math.round(velocita * 10) / 10,
        tempo_percorrenza: d.travel_time_in_seconds,
        tempo_storico: d.historic_traffic_travel_time_in_seconds,
        lunghezza_coda: d.traffic_length_in_meters,
      };
    });
  */

  const getPopupWidthClass = () => {
    if (siderSection === "video") return "width-video";
    if (siderSection === "proprieta") return "width-proprieta";
    // Network view cards are smaller
    if (
      siderSection === "velocita" &&
      (!selectedHash || selectedHash.length === 0)
    )
      return "width-default";
    if (
      siderSection === "flow" &&
      (!selectedHash || selectedHash.length === 0)
    )
      return "width-default";
    if (
      siderSection === "traveltime" &&
      (!selectedHash || selectedHash.length === 0)
    )
      return "width-default";
    if (siderSection === "velocita") return "width-proprieta";
    if (siderSection === "flow") return "width-proprieta";
    if (siderSection === "traveltime") return "width-proprieta";
    if (siderSection === "ambientali") return "width-ambientali";
    if (siderSection === "accelerometrici") return "width-accelerometrici";
    if (siderSection === "audio") return "width-audio";
    if (siderSection === "confronto") return "width-proprieta";
    return "width-default";
  };

  return (
    <Layout
      className={`geojson-layout ${
        isMobile ? "geojson-layout-mobile" : "geojson-layout-desktop"
      }`}
    >
      {/**
       * Sidebar commented out for full-screen map
       * <SiderComponent
       *   isMobile={isMobile}
       *   collapsed={collapsed}
       *   setCollapsed={setCollapsed}
       *   siderSection={siderSection}
       *   selectedFeature={selectedFeature}
       *   arco={arco}
       *   availableDates={availableDates}
       *   selectedDate={selectedDate}
       *   setSelectedDate={setSelectedDate}
       *   selectedData={selectedData}
       * />
       */}

      <Layout className="geojson-inner-layout">
        <Content className="geojson-content">
          {/* Betwin Logo - Top Left */}
          <div className="betwin-logo-container">
               <img src={BetwinLogo} alt="Lavagna Logo" className="betwin-logo-img" />
            <span className="betwin-logo-text">Lavagna</span>
          </div>

          <div className="geojson-map-container">
            <MapComponent
              style={{
                padding: 0,
                borderRadius: 0,
                height: "100%",
                // keep part of the handle tappable even when closed
                paddingBottom: footerOpen ? FOOTER_HEIGHT : 0,
                overflow: "hidden",
                position: "relative",
              }}
              geoData={geoData}
              onFeatureSelect={onFeatureSelect}
              styleFeature={styleFeature}
              speedDomain={speedDomain}
              siderSection={siderSection}
              setSiderSection={setSiderSection}
              showDeckGL={showDeckGL}
              setShowDeckGL={setShowDeckGL}
              mapMode={mapMode}
              setMapMode={setMapMode}
              colorScale={colorScale}
              availableDates={availableDates}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedHash={selectedHash}
              setSelectedHash={setSelectedHash}
              setSelectedFeature={setSelectedFeature}
              onRadarSelect={onRadarSelect}
              radarSelected={radarSelected}
              setRadarSelected={setRadarSelected}
              footerOpen={footerOpen}
              footerHeight={FOOTER_HEIGHT}
              isLoadingGeoData={isLoadingGeoData}
              networkViewCategory={networkViewCategory}
              setNetworkViewCategory={setNetworkViewCategory}
              networkViewDate={networkViewDate}
              setNetworkViewDate={setNetworkViewDate}
              networkViewTime={networkViewTime}
              setNetworkViewTime={setNetworkViewTime}
              availableNetworkDates={availableNetworkDates}
              availableNetworkTimes={availableNetworkTimes}
            />
            {/** Popup card overlay with Sider content */}
            {mapMode === "mappa" && siderSection && (
              <div className={`geojson-popup-card ${getPopupWidthClass()}`}>
                <button
                  onClick={() => setSiderSection(null)}
                  className="geojson-popup-close-btn"
                  aria-label="Close"
                >
                  ✕
                </button>
                <SiderComponent
                  isMobile={true}
                  collapsed={false}
                  setCollapsed={() => {}}
                  siderSection={siderSection}
                  selectedFeature={selectedFeature}
                  networkViewDate={networkViewDate}
                  setNetworkViewDate={setNetworkViewDate}
                  networkViewTime={networkViewTime}
                  setNetworkViewTime={setNetworkViewTime}
                  availableNetworkDates={availableNetworkDates}
                  availableNetworkTimes={availableNetworkTimes}
                  networkViewCategory={networkViewCategory}
                  arco={
                    selectedHash && selectedHash.length > 0
                      ? (() => {
                          const filteredData =
                            dbData
                              ?.filter((d) => selectedHash.includes(d.hash) || selectedHash.includes(d.UnifiedID))
                              .map((d) => {
                                // Normalize data format for compatibility with child components
                                const normalizedData = { ...d };

                                // Ensure date field exists
                                if (d.timestamp_utc && !d.date) {
                                  // New format: convert timestamp_utc to date
                                  normalizedData.date =
                                    d.timestamp_utc.split(" ")[0] +
                                    "T00:00:00.000";
                                }

                                // Ensure timestamp field exists (Unix timestamp in seconds)
                                if (d.time && !d.timestamp) {
                                  normalizedData.timestamp = parseInt(d.time);
                                }

                                // Parse numeric strings to numbers for consistency
                                if (typeof d.length_in_meters === "string") {
                                  normalizedData.length_in_meters = parseFloat(
                                    d.length_in_meters
                                  );
                                }
                                if (
                                  typeof d.travel_time_in_seconds === "string"
                                ) {
                                  normalizedData.travel_time_in_seconds =
                                    parseFloat(d.travel_time_in_seconds);
                                }
                                if (
                                  typeof d.historic_traffic_travel_time_in_seconds ===
                                  "string"
                                ) {
                                  normalizedData.historic_traffic_travel_time_in_seconds =
                                    parseFloat(
                                      d.historic_traffic_travel_time_in_seconds
                                    );
                                }
                                if (
                                  typeof d.traffic_length_in_meters === "string"
                                ) {
                                  normalizedData.traffic_length_in_meters =
                                    parseFloat(d.traffic_length_in_meters);
                                }
                                if (
                                  typeof d.live_traffic_incidents_travel_time_in_seconds ===
                                  "string"
                                ) {
                                  normalizedData.live_traffic_incidents_travel_time_in_seconds =
                                    parseFloat(
                                      d.live_traffic_incidents_travel_time_in_seconds
                                    );
                                }
                                if (
                                  typeof d.traffic_delay_in_seconds === "string"
                                ) {
                                  normalizedData.traffic_delay_in_seconds =
                                    parseFloat(d.traffic_delay_in_seconds);
                                }

                                return normalizedData;
                              }) || [];

                          // Check which selected roads are missing from filteredData
                          // and create minimal objects for them so components know they ARE selected
                          if (
                            selectedHash &&
                            selectedHash.length > 0 &&
                            selectedFeature &&
                            selectedFeature.length > 0
                          ) {
                            // Get hashes that exist in filteredData
                            const hashesWithData = new Set(
                              filteredData.map((d) => d.hash)
                            );

                            // Find roads that are selected but have no data
                            const roadsWithoutData = selectedFeature.filter(
                              (feature) => !hashesWithData.has(feature.hash)
                            );

                            // Create minimal objects for roads without data
                            const minimalObjects = roadsWithoutData.map(
                              (feature) => ({
                                hash: feature.hash,
                                // Include basic properties from GeoJSON
                                ...(feature.StreetName && {
                                  StreetName: feature.StreetName,
                                }),
                                ...(feature.Length && {
                                  length_in_meters: feature.Length,
                                }),
                                ...(feature.SpeedLimit && {
                                  speed_limit: feature.SpeedLimit,
                                }),
                              })
                            );

                            // Combine data: roads with data + roads without data
                            return [...filteredData, ...minimalObjects];
                          }

                          return filteredData;
                        })()
                      : [] // Empty array when no road is selected
                  }
                  geoData={geoData}
                  availableDates={availableDates}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  radarSelected={radarSelected}
                  selectedData={
                    selectedHash && selectedHash.length > 0
                      ? dbData
                          ?.filter((d) => {
                            if (!selectedHash.includes(d.hash)) return false;
                            if (!selectedDate) return false;

                            // Support both date formats
                            if (d.timestamp_utc) {
                              return d.timestamp_utc.startsWith(selectedDate);
                            } else if (d.date) {
                              return d.date.startsWith(selectedDate);
                            }
                            return false;
                          })
                          ?.sort((a, b) => {
                            const timeA = a.timestamp || parseInt(a.time) || 0;
                            const timeB = b.timestamp || parseInt(b.time) || 0;
                            return timeA - timeB;
                          })
                          ?.map((d) => {
                            const timestamp =
                              d.timestamp || parseInt(d.time) || 0;
                            const date = new Date(timestamp * 1000);
                            const hh = String(date.getHours()).padStart(2, "0");
                            const mm = String(date.getMinutes()).padStart(
                              2,
                              "0"
                            );
                            const ss = String(date.getSeconds()).padStart(
                              2,
                              "0"
                            );
                            const orario = `${hh}:${mm}:${ss}`;

                            let velocita = 0;
                            if (d.speed) {
                              // New format has speed directly
                              velocita = parseFloat(d.speed);
                            } else if (
                              d.travel_time_in_seconds &&
                              parseFloat(d.travel_time_in_seconds) > 0
                            ) {
                              // Old format: calculate speed
                              velocita =
                                (parseFloat(d.length_in_meters) /
                                  parseFloat(d.travel_time_in_seconds)) *
                                3.6;
                            }

                            return {
                              time: orario,
                              velocità: Math.round(velocita * 10) / 10,
                              tempo_percorrenza:
                                parseFloat(d.travel_time_in_seconds) || 0,
                              tempo_storico:
                                parseFloat(
                                  d.historic_traffic_travel_time_in_seconds
                                ) || 0,
                              lunghezza_coda:
                                parseFloat(d.traffic_length_in_meters) || 0,
                            };
                          })
                      : [] // Empty array when no road is selected
                  }
                />
              </div>
            )}

            {/** Floating toggle button (visible in both states) */}
            <button
              onClick={() => setFooterOpen(!footerOpen)}
              className={`geojson-footer-toggle ${
                isMobile ? "geojson-footer-toggle-mobile" : ""
              }`}
              style={{ bottom: footerOpen ? FOOTER_HEIGHT - 15 : 12 }}
              aria-label="Toggle footer"
              title={footerOpen ? "Nascondi" : "Mostra"}
            >
              {footerOpen ? <FaLongArrowAltDown /> : <FaLongArrowAltUp />}
            </button>

            {footerOpen && (
              /** Bottom footer slider overlay */
              <div className="geojson-footer-slider">
                <div
                  className={`geojson-footer-content ${
                    isMobile ? "geojson-footer-content-mobile" : ""
                  }`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '12px 24px'
                  }}
                >
                  <div
                    className={`geojson-footer-copyright ${
                      isMobile ? "size-xs" : "size-default"
                    }`}
                    style={{
                      fontSize: '14px',
                      color: '#666'
                    }}
                  >
                    © 2025 Tutti i diritti riservati.
                  </div>
                </div>
              </div>
            )}

            {/** Toggle is now handled inside the slider card */}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default GeoJson;
